import React, { useEffect, useState } from "react";

//Local components
import ErrorBox from "../Components/ErrorBox";
import Selector from "../Components/basics/Selector";
import SetItemDrawer from "../Components/drawers/SetItemDrawer";
import ItemRow from "../Components/inventory-page/ItemRow";

//UI Material
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

//Material Icons
import { Add, ArrowBackIos, Search, Settings } from "@mui/icons-material";

//Helpers
import { searchEngine } from "../Helpers/searchEngine";
import { fetchWithTokenJSONPost } from "../Helpers/network";

//Local components
import { a11yProps, TabPanel } from "../Components/basics/TabPanel";
import EntriesTable from "../Components/inventory-page/EntriesTable";
import StockoutsTable from "../Components/inventory-page/StockoutsTable";

export default function Inventory() {
  // ==========>Component States<==========
  //State for open/close drawer for set product
  const [openDrawer, setOpenDrawer] = useState(false);
  //State for select page
  const [page, setPage] = useState(0);
  //State for manage rows per page
  const [rowsPerPage, setRowsPerPage] = useState(10);
  //State for setting error when could not get products data
  const [dataError, setDataError] = useState(false);
  //State for categories loader
  const [categoriesLoader, setCategoriesLoader] = useState(true);
  //State for items loader
  const [itemsLoader, setItemsLoader] = useState(true);
  //State for search data by query
  const [searchQuery, setSearchQuery] = useState("");
  //State for items list
  const [itemsList, setItemsList] = useState([]);
  //State for categories list
  const [categoriesList, setCategoriesList] = useState([]);
  //State for filter selection
  const [itemCategorySelection, setItemCategorySelection] = useState(0);
  // State for tab value
  const [tabValue, setTabValue] = useState(0);
  //State for suppliers data
  const [suppliersList, setSuppliersList] = useState([]);

  //The useEffect checks for token validation
  useEffect(() => {
    getCategoriesList();
    getItemsList();
    getSuppliersList();
  }, []);

  // ==========>Component Functions<==========
  //Function to manage page selection
  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  //Function to control rows per page options
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  //Function for data query
  const filterData = (query, data) => {
    if (!query) {
      return data;
    } else {
      return searchEngine(data, query);
    }
  };

  //Function to select filtered data
  const filterItemsByType = () => {
    try {
      return itemsList.filter(
        (item) => item.category === categoriesList[itemCategorySelection].name
      );
    } catch (e) {
      return itemsList;
    }
  };

  //Data in the table
  const dataFiltered = filterData(searchQuery, filterItemsByType());

  //Function to get items list
  const getSuppliersList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/inventory/get-suppliers`,
        {
          howMany: 2500,
        }
      );
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setSuppliersList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      // setDataError(false);
    }
  };

  //Function to get items list
  const getCategoriesList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/inventory/get-categories`,
        {
          howMany: 2500,
        }
      );
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setCategoriesList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setDataError(false);
    }
    setCategoriesLoader(false);
  };

  //Function to get items list
  const getItemsList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/inventory/get-items`,
        {
          howMany: 2500,
        }
      );
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setItemsList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setDataError(false);
    }
    setItemsLoader(false);
  };

  const lowStockItemsCount = itemsList.filter(
    (item) => item.availableStock < item.recommendedStock
  ).length;

  // ==========>Component<==========
  return dataError ? (
    <ErrorBox />
  ) : (
    <Grid container spacing={1}>
      <SetItemDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        getData={getItemsList}
        isNewItem={true}
      />
      <Grid item xs={12}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Button
            startIcon={<ArrowBackIos />}
            color="secondary"
            sx={{ color: "white" }}
            variant="contained"
            size="small"
            onClick={() => {
              window.history.back();
            }}
          >
            Volver
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            sx={{ color: "white" }}
            endIcon={<Add />}
            onClick={() => {
              setOpenDrawer(true);
            }}
          >
            nuevo ítem
          </Button>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Card raised>
          <Grid container>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Lista de ítems en inventario
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  spacing={1}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {itemsList.length}
                  </Typography>
                  <Typography variant="h7">
                    ítems registrados en inventario
                  </Typography>
                </Stack>
                <Alert severity="error">{`Ítems con stock menor al recomendado: ${lowStockItemsCount}`}</Alert>
              </Stack>
            </Grid>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <Typography variant="body2" fontWeight="bold">
                    Categoría de ítem
                  </Typography>
                  {categoriesLoader ? (
                    <Stack justifyContent="flex-start" alignItems="flex-start">
                      <CircularProgress />
                    </Stack>
                  ) : (
                    <Selector
                      width="100%"
                      content={categoriesList.map((cat) => cat.name)}
                      value={itemCategorySelection}
                      handleChange={(e) => {
                        setItemCategorySelection(e.target.value);
                      }}
                    />
                  )}
                </Stack>
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <Typography variant="body2" fontWeight="bold">
                    Buscar un ítem
                  </Typography>
                  <TextField
                    id="search-bar"
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    variant="outlined"
                    placeholder="Buscar..."
                    fullWidth
                    InputProps={{
                      endAdornment: <Search />,
                    }}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <TableContainer>
                <Table aria-label="items-table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          ID
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          Nombre del ítem
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          Estante
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          Cantidad en stock
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          Fecha última entrada
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          Fecha última salida
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold">
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {itemsLoader ? (
                      <TableCell align="center" colSpan={7}>
                        <CircularProgress />
                      </TableCell>
                    ) : dataFiltered.length ? (
                      dataFiltered
                        .sort((a, b) => {
                          if (a.name < b.name) return -1;
                          if (a.name > b.name) return 1;
                          return 0;
                        })
                        .map((item) => {
                          const needsStockAlert =
                            item.availableStock < item.recommendedStock;
                          return (
                            <ItemRow
                              key={`${item.id}-${item.createdAt}`}
                              item={item}
                              getData={getItemsList}
                              suppliersList={suppliersList}
                              needsStockAlert={needsStockAlert}
                            />
                          );
                        })
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                    ) : (
                      <TableRow>
                        <TableCell align="center" colSpan={7}>
                          <Typography variant="body2" fontWeight="bold">
                            No hay ítems registrados
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={dataFiltered.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                labelRowsPerPage="Filas"
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                sx={{ color: "white" }}
                endIcon={<Settings />}
                onClick={() => {
                  window.location.href = "/item-categories";
                }}
              >
                Administrar categorías de items
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <Card raised>
          <Grid container>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Movimientos de inventario
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ p: 2 }}>
              <Box>
                <Tabs
                  centered
                  indicatorColor="secondary"
                  textColor="secondary"
                  value={tabValue}
                  onChange={handleChange}
                  aria-label="product-tabs"
                >
                  <Tab
                    label="Entradas"
                    sx={{ fontSize: "11px" }}
                    {...a11yProps(0)}
                  />
                  <Tab
                    label="Salidas"
                    sx={{ fontSize: "11px" }}
                    {...a11yProps(1)}
                  />
                </Tabs>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <TabPanel value={tabValue} index={0}>
                <EntriesTable />
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                <StockoutsTable />
              </TabPanel>
            </Grid>
          </Grid>
        </Card>
      </Grid>
    </Grid>
  );
}
